import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRightIcon } from '@heroicons/react/24/outline';
import Hero from '../components/Hero';
import Services from '../components/Services';
import introImage from '../assets/images/PHOTO-2025-07-27-10-08-11.jpg';
import g1 from '../assets/images/PHOTO-2025-07-27-10-08-32.jpg';
import g2 from '../assets/images/PHOTO-2025-07-27-10-08-32 2.jpg';
import g3 from '../assets/images/PHOTO-2025-07-27-10-08-32 3.jpg';
import g4 from '../assets/images/PHOTO-2025-07-27-10-08-32 4.jpg';

const galleryTeaser = [g1, g2, g3, g4];

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0 },
};

const Home = () => {
  const scrollTopAfterNav = () => {
    setTimeout(() => {
      try {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } catch (_e) {
        window.scrollTo(0, 0);
      }
    }, 100);
  };

  return (
    <div className="home-page">
      <Hero />

      {/* Intro – clean, white */}
      <section className="section bg-white dark:bg-wood-950">
        <div className="container">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: '-80px' }}
              variants={fadeInUp}
              transition={{ duration: 0.7 }}
            >
              <span className="block text-xs uppercase tracking-[0.2em] text-gold-600 dark:text-gold-300 font-semibold mb-3">
                Willkommen
              </span>
              <h2 className="font-display text-3xl md:text-4xl lg:text-5xl text-wood-900 dark:text-cream-50 text-balance">
                Tradition trifft moderne <span className="gradient-text">Lasertechnik</span>
              </h2>
              <p className="mt-6 text-wood-700 dark:text-cream-100/80 text-lg leading-relaxed">
                Bei SS-craftedwithlove verbinde ich traditionelles Handwerk mit moderner
                Lasertechnologie, um einzigartige Holzgravuren zu erschaffen, die Geschichten
                erzählen und Erinnerungen bewahren.
              </p>
              <p className="mt-4 text-wood-600 dark:text-cream-100/80 leading-relaxed">
                Jedes Stück wird mit Liebe zum Detail in der Werkstatt in Daiting gefertigt –
                von personalisierten Geschenken über Dekorationen bis hin zu praktischen
                Alltagsgegenständen.
              </p>
              <Link
                to="/ueber-uns"
                onClick={scrollTopAfterNav}
                className="btn btn-link mt-6"
              >
                Mehr über mich
                <ArrowRightIcon className="w-4 h-4" />
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, margin: '-80px' }}
              transition={{ duration: 0.8 }}
              className="relative"
            >
              <div className="relative rounded-3xl overflow-hidden shadow-wood">
                <img
                  src={introImage}
                  alt="Holzlasergravur Beispiel"
                  className="w-full h-full object-cover aspect-[4/5]"
                  loading="lazy"
                />
              </div>
              <div
                className="hidden md:block absolute -bottom-6 -left-6 w-32 h-32 rounded-2xl bg-gold-500/20 backdrop-blur-md ring-1 ring-gold-300/30 -z-10"
                aria-hidden="true"
              />
              <div
                className="hidden md:block absolute -top-6 -right-6 w-28 h-28 rounded-2xl bg-wood-500/20 -z-10"
                aria-hidden="true"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services – warmes Cream mit Texture */}
      <section className="section bg-cream-100 dark:bg-wood-900 relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.06] dark:opacity-[0.08] pointer-events-none"
          style={{
            backgroundImage:
              "radial-gradient(circle at 1px 1px, rgba(139,90,49,0.6) 1px, transparent 0)",
            backgroundSize: '32px 32px',
          }}
          aria-hidden="true"
        />
        <div className="container relative">
          <div className="section-heading">
            <span className="block text-xs uppercase tracking-[0.2em] text-gold-600 dark:text-gold-300 font-semibold mb-3">
              Was ich biete
            </span>
            <h2 className="section-title">Meine Leistungen</h2>
            <p className="section-subtitle">
              Entdecken Sie meine vielfältigen Angebote im Bereich der Holzlasergravur
            </p>
          </div>

          <Services />

          <div className="text-center mt-12">
            <Link
              to="/leistungen"
              onClick={scrollTopAfterNav}
              className="btn btn-primary"
            >
              Alle Leistungen entdecken
              <ArrowRightIcon className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Gallery teaser – DARK Showcase */}
      <section className="section bg-wood-900 relative overflow-hidden">
        <div
          className="absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full bg-gold-500/10 blur-3xl"
          aria-hidden="true"
        />
        <div
          className="absolute -bottom-40 -left-40 w-[500px] h-[500px] rounded-full bg-wood-600/30 blur-3xl"
          aria-hidden="true"
        />
        <div className="container relative">
          <div className="mb-12 text-center max-w-3xl mx-auto">
            <span className="block text-xs uppercase tracking-[0.2em] text-gold-300 font-semibold mb-3">
              Galerie
            </span>
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl text-cream-50 text-balance relative inline-block">
              Ein Einblick in meine Arbeiten
              <span
                className="absolute left-1/2 -translate-x-1/2 -bottom-3 w-20 h-[3px] rounded-full bg-gradient-to-r from-gold-400 to-gold-500"
                aria-hidden="true"
              />
            </h2>
            <p className="mt-6 text-cream-100/70 text-lg">
              Eine kleine Auswahl handgefertigter Holzkunstwerke
            </p>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {galleryTeaser.map((src, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-80px' }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="group relative aspect-square rounded-2xl overflow-hidden ring-1 ring-wood-700/60 shadow-2xl hover:ring-gold-400/60 transition-all duration-500"
              >
                <img
                  src={src}
                  alt={`Galeriebild ${index + 1}`}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-wood-950/80 via-wood-950/20 to-transparent opacity-70 group-hover:opacity-30 transition-opacity" />
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/galerie"
              onClick={scrollTopAfterNav}
              className="btn btn-light"
            >
              Zur vollständigen Galerie
              <ArrowRightIcon className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section relative overflow-hidden">
        <div className="absolute inset-0 bg-wood-gradient" aria-hidden="true" />
        <div
          className="absolute inset-0 opacity-30 mix-blend-overlay"
          style={{
            backgroundImage:
              "radial-gradient(circle at 20% 30%, rgba(255,255,255,0.2), transparent 40%), radial-gradient(circle at 80% 70%, rgba(212,175,55,0.4), transparent 50%)",
          }}
          aria-hidden="true"
        />
        <div className="container relative">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.7 }}
            className="max-w-3xl mx-auto text-center text-cream-50"
          >
            <h2 className="font-display text-cream-50 text-3xl md:text-4xl lg:text-5xl text-balance">
              Haben Sie eine Idee für Ihre persönliche Gravur?
            </h2>
            <p className="mt-6 text-cream-50/90 text-lg">
              Kontaktieren Sie mich noch heute, um Ihre individuellen Wünsche zu besprechen.
              Ich freue mich darauf, Ihre Ideen in einzigartige Holzkunstwerke umzusetzen.
            </p>
            <Link
              to="/kontakt"
              onClick={scrollTopAfterNav}
              className="btn btn-light mt-8 inline-flex"
            >
              Jetzt anfragen
              <ArrowRightIcon className="w-4 h-4" />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home;
